import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  InternalAuditReportRegisterResponse,
  InternalAuditReportRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditReportsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC internal audit report data to match the GraphQL query structure
 */
function mapTrpcInternalAuditReportToGraphQL(
  internalAuditReport: InternalAuditReportRegisterResponseRow
): GetInternalAuditReportsQuery['internal_audit_report'][number] {
  return {
    ...internalAuditReport,
    assessedItems: internalAuditReport.assessmentResults || [], // Map assessmentResults to assessedItems
  };
}

/**
 * Maps TRPC internal audit reports data to match the GraphQL query structure
 */
function mapTrpcInternalAuditReportsToGraphQL(
  trpcData: InternalAuditReportRegisterResponse | undefined
): GetInternalAuditReportsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    internal_audit_report: trpcData.internal_audit_report.map((report) =>
      mapTrpcInternalAuditReportToGraphQL(report)
    ),
  };
}

export const useGetInternalAuditReportsRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditReport.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcInternalAuditReportsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
